// Purpose-drawn icons distinguishing the two Process Studio resource types —
// FlowSnip (a diagram built with the swimlane/phase-flow engine) and PageSnip
// (a ready-made rich page shown as-is). Every other site in the app used a
// borrowed generic lucide icon (Workflow for both, or FileText) which didn't
// read as two distinct types at a glance; these replace them everywhere both
// kinds are represented. Same Lucide-compatible API as ThreadIcon.tsx
// (size / strokeWidth / className, currentColor stroke) so they drop in
// wherever a lucide icon was used, and stay legible at the 13–16px this app
// actually renders them.
//
// Color is deliberately NOT baked in here — some callers (e.g. ContextChip's
// context-type pill) tint these via an ancestor's inherited `color` and would
// have that overridden by a class on the SVG itself. Callers that want the
// canonical Worksnip-orange / PageSnip-lime-green identity color apply
// `ws-worksnip-ink` / `ws-pagesnip-ink` from snip-type-icons.css (imported
// below as a side effect so it's available wherever these icons are used).
// Outline/stroke only, no filled background chip around the glyph — that's a
// className the CALLER's wrapper adds (or doesn't), never these components.
import './snip-type-icons.css';
import { Film } from 'lucide-react';

interface Props {
  size?: number;
  className?: string;
  strokeWidth?: number;
}

/** Worksnip — a training guide/capture. Lucide's `Film` glyph (the frame
 *  icon, not a literal "video" reading at this size) gives worksnips their
 *  own silhouette distinct from both PageSnip glyphs below; pair with the
 *  `ws-worksnip-ink` class (snip-type-icons.css) for the canonical color. */
export function WorksnipIcon({ size = 16, className, strokeWidth = 1.75 }: Props) {
  return <Film size={size} className={className} strokeWidth={strokeWidth} aria-hidden="true" />;
}

/** FlowSnip — a phase-flow diagram: two boxes joined by a connector, with a
 *  third box branching down from the second. Reads as "connected process
 *  steps" even at 14px, distinct from PageSnip's single document silhouette. */
export function FlowSnipIcon({ size = 16, className, strokeWidth = 2 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Node 1 (top-left) → connector → Node 2 (top-right). */}
      <rect x="3" y="4" width="7" height="5" rx="1.2" />
      <rect x="14" y="4" width="7" height="5" rx="1.2" />
      <path d="M10 6.5h4" />
      {/* Branch down from Node 2 to Node 3 (bottom-right). */}
      <rect x="14" y="15" width="7" height="5" rx="1.2" />
      <path d="M17.5 9v6" />
    </svg>
  );
}

/** PageSnip — a rich document page: a page frame with a fixed left nav strip
 *  (echoing the PageSnip viewer's own left nav), a bold heading bar, and text
 *  lines. Reads as "a page" even at 14px, distinct from FlowSnip's node/
 *  connector silhouette. */
export function PageSnipIcon({ size = 16, className, strokeWidth = 2 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Page frame + the fixed left-nav divider. */}
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M8 3v18" />
      {/* Heading bar (filled, so it reads bolder than the text lines below it). */}
      <rect x="11" y="6.4" width="7" height="1.6" rx="0.8" fill="currentColor" stroke="none" />
      {/* Body text lines. */}
      <path d="M11 11h7" />
      <path d="M11 14.5h5" />
    </svg>
  );
}
